import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveEpisode, getAllEpisodes, deleteEpisode } from '../podcastDb';
import './Podcasts.css';

const LANGUAGES = [
  { code: 'FR', label: 'Français' },
  { code: 'US', label: 'Anglais (US)' },
  { code: 'GB', label: 'Anglais (UK)' },
  { code: 'ES', label: 'Espagnol' },
  { code: 'DE', label: 'Allemand' },
  { code: 'IT', label: 'Italien' },
  { code: 'PT', label: 'Portugais' },
  { code: 'JP', label: 'Japonais' },
];

const SUGGESTED_KEYWORDS = [
  'Actualité', 'True crime', 'Sport', 'Cinéma', 'Histoire', 'Science', 'Comédie', 'Société',
];

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
};

const formatDuration = (d) => {
  if (!d) return '';
  if (d.includes(':')) return d;
  const secs = parseInt(d, 10);
  if (Number.isNaN(secs)) return '';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const formatDate = (raw) => {
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR');
};

const Podcasts = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('search');
  const [previousView, setPreviousView] = useState('search');

  const [term, setTerm] = useState('');
  const [country, setCountry] = useState('FR');
  const [podcasts, setPodcasts] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searched, setSearched] = useState(false);

  const [trending, setTrending] = useState([]);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [trendingError, setTrendingError] = useState(null);

  const [selectedPodcast, setSelectedPodcast] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [episodesLoading, setEpisodesLoading] = useState(false);
  const [episodesError, setEpisodesError] = useState(null);

  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [downloadedIds, setDownloadedIds] = useState(new Set());
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  const [downloads, setDownloads] = useState([]);

  const objectUrlRef = useRef(null);

  useEffect(() => { refreshDownloads(); }, []);

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  useEffect(() => { fetchTrending(country); }, [country]);

  const refreshDownloads = async () => {
    const list = await getAllEpisodes();
    setDownloads(list);
    setDownloadedIds(new Set(list.map((e) => e.id)));
  };

  const fetchTrending = async (c) => {
    setTrendingLoading(true);
    setTrendingError(null);
    try {
      const res = await fetch(`/privateZone/podcast_top.php?country=${c}`, { credentials: 'include' });
      if (res.status === 401) { navigate('/login'); return; }
      const data = await res.json();
      setTrending(data.podcasts || []);
    } catch {
      setTrendingError('Tendances indisponibles pour le moment.');
    } finally {
      setTrendingLoading(false);
    }
  };

  const performSearch = async (searchTerm) => {
    if (!searchTerm.trim()) return;
    setSearchLoading(true);
    setSearchError(null);
    setSearched(true);
    try {
      const res = await fetch(`/privateZone/podcast_search.php?term=${encodeURIComponent(searchTerm)}&country=${country}`, { credentials: 'include' });
      if (res.status === 401) { navigate('/login'); return; }
      const data = await res.json();
      setPodcasts(data.podcasts || []);
    } catch {
      setSearchError('Recherche indisponible pour le moment.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    performSearch(term);
  };

  const handleSuggestionClick = (keyword) => {
    setTerm(keyword);
    performSearch(keyword);
  };

  const clearSearch = () => {
    setTerm('');
    setSearched(false);
    setPodcasts([]);
    setSearchError(null);
  };

  const openPodcast = async (podcast) => {
    setSelectedPodcast(podcast);
    setEpisodes([]);
    setEpisodesError(null);
    setEpisodesLoading(true);
    setView('episodes');
    try {
      const res = await fetch(`/privateZone/podcast_episodes.php?feedUrl=${encodeURIComponent(podcast.feedUrl)}`, { credentials: 'include' });
      if (res.status === 401) { navigate('/login'); return; }
      if (!res.ok) { setEpisodesError('Impossible de charger ce podcast (flux invalide ou indisponible).'); return; }
      const data = await res.json();
      setEpisodes(data.episodes || []);
    } catch {
      setEpisodesError('Impossible de charger ce podcast.');
    } finally {
      setEpisodesLoading(false);
    }
  };

  const backToSearch = () => {
    setView('search');
    setSelectedPodcast(null);
    setEpisodes([]);
  };

  const openDownloads = () => {
    setPreviousView(view);
    setView('downloads');
  };

  const closeDownloads = () => setView(previousView);

  const setPlayerSrc = (title, podcastName, src, isOffline, id) => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    if (isOffline) objectUrlRef.current = src;
    setCurrentEpisode({ id, title, podcastName, src, isOffline });
  };

  const playEpisode = async (ep, podcastName) => {
    if (downloadedIds.has(ep.id)) {
      const stored = downloads.find((d) => d.id === ep.id);
      if (stored) {
        const url = URL.createObjectURL(stored.blob);
        setPlayerSrc(ep.title, podcastName, url, true, ep.id);
        return;
      }
    }
    const src = `/privateZone/podcast_stream.php?url=${encodeURIComponent(ep.audioUrl)}`;
    setPlayerSrc(ep.title, podcastName, src, false, ep.id);
  };

  const playDownload = (d) => {
    const url = URL.createObjectURL(d.blob);
    setPlayerSrc(d.title, d.podcastName, url, true, d.id);
  };

  const downloadEpisode = async (ep) => {
    setDownloadingIds((prev) => new Set(prev).add(ep.id));
    try {
      const res = await fetch(`/privateZone/podcast_stream.php?url=${encodeURIComponent(ep.audioUrl)}`, { credentials: 'include' });
      if (res.status === 401) { navigate('/login'); return; }
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      await saveEpisode({
        id: ep.id,
        podcastName: selectedPodcast?.name || '',
        title: ep.title,
        pubDate: ep.pubDate,
        mimeType: ep.mimeType,
      }, blob);
      await refreshDownloads();
    } catch {
      setEpisodesError('Échec du téléchargement de cet épisode.');
    } finally {
      setDownloadingIds((prev) => { const next = new Set(prev); next.delete(ep.id); return next; });
    }
  };

  const removeDownload = async (id) => {
    await deleteEpisode(id);
    if (currentEpisode?.id === id && currentEpisode.isOffline) {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
      setCurrentEpisode(null);
    }
    await refreshDownloads();
  };

  const handleLogout = async () => {
    await fetch('/privateZone/logout.php', { credentials: 'include' });
    navigate('/login');
  };

  const headerTitle = view === 'downloads' ? 'Téléchargements' : (view === 'episodes' ? (selectedPodcast?.name || 'Épisodes') : 'Podcasts');

  return (
    <div className="podcast-container">
      <div className="podcast-header">
        {view !== 'search' && (
          <button className="podcast-back" onClick={view === 'downloads' ? closeDownloads : backToSearch} title="Retour">←</button>
        )}
        <h2 className="podcast-title">{headerTitle}</h2>
        {view !== 'downloads' && (
          <button className="podcast-downloads-btn" onClick={openDownloads} title="Téléchargements">
            ⬇ {downloads.length > 0 ? downloads.length : ''}
          </button>
        )}
        <button className="podcast-logout" onClick={handleLogout} title="Déconnexion">⏻</button>
      </div>

      {view === 'search' && (
        <>
          <form className="podcast-search-form" onSubmit={handleSearch}>
            <input
              className="podcast-search-input"
              type="text"
              placeholder="Mots-clés..."
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
            <select className="podcast-lang-select" value={country} onChange={(e) => setCountry(e.target.value)}>
              {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            <button className="podcast-search-btn" type="submit">Chercher</button>
            {searched && (
              <button type="button" className="podcast-clear-btn" onClick={clearSearch} title="Effacer la recherche">✕</button>
            )}
          </form>

          {!searched && (
            <div className="podcast-suggestions">
              {SUGGESTED_KEYWORDS.map((k) => (
                <button key={k} className="podcast-chip" onClick={() => handleSuggestionClick(k)}>{k}</button>
              ))}
            </div>
          )}

          {searchLoading && <div className="podcast-status">Recherche...</div>}
          {searchError && <div className="podcast-status podcast-error">{searchError}</div>}
          {!searchLoading && searched && podcasts.length === 0 && !searchError && (
            <div className="podcast-status">Aucun résultat.</div>
          )}

          {searched ? (
            <ul className="podcast-list">
              {podcasts.map((p) => (
                <li key={p.id} className="podcast-item" onClick={() => openPodcast(p)}>
                  {p.artwork ? <img className="podcast-artwork" src={p.artwork} alt="" /> : <div className="podcast-artwork placeholder" />}
                  <div className="podcast-item-info">
                    <div className="podcast-item-name">{p.name}</div>
                    <div className="podcast-item-artist">{p.artist}{p.genre ? ` · ${p.genre}` : ''}</div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <>
              <div className="podcast-section-title">🔥 Tendances ({country})</div>
              {trendingLoading && <div className="podcast-status">Chargement...</div>}
              {trendingError && <div className="podcast-status podcast-error">{trendingError}</div>}
              <ul className="podcast-list">
                {trending.map((p, i) => (
                  <li key={p.id} className="podcast-item" onClick={() => openPodcast(p)}>
                    <span className="podcast-rank">{i + 1}</span>
                    {p.artwork ? <img className="podcast-artwork" src={p.artwork} alt="" /> : <div className="podcast-artwork placeholder" />}
                    <div className="podcast-item-info">
                      <div className="podcast-item-name">{p.name}</div>
                      <div className="podcast-item-artist">{p.artist}{p.genre ? ` · ${p.genre}` : ''}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {view === 'episodes' && (
        <>
          {episodesLoading && <div className="podcast-status">Chargement des épisodes...</div>}
          {episodesError && <div className="podcast-status podcast-error">{episodesError}</div>}
          <ul className="podcast-list podcast-episode-list">
            {episodes.map((ep) => {
              const isDownloaded = downloadedIds.has(ep.id);
              const isDownloading = downloadingIds.has(ep.id);
              return (
                <li key={ep.id} className={`podcast-episode-item ${currentEpisode?.id === ep.id ? 'playing' : ''}`}>
                  <div className="podcast-episode-main" onClick={() => playEpisode(ep, selectedPodcast?.name)}>
                    <span className="podcast-episode-indicator">{currentEpisode?.id === ep.id ? '▶' : '○'}</span>
                    <div className="podcast-episode-text">
                      <div className="podcast-episode-title">{ep.title}</div>
                      <div className="podcast-episode-meta">
                        {formatDate(ep.pubDate)}{ep.duration ? ` · ${formatDuration(ep.duration)}` : ''}{ep.size ? ` · ${formatSize(ep.size)}` : ''}
                      </div>
                    </div>
                  </div>
                  <button
                    className={`podcast-download-btn ${isDownloaded ? 'done' : ''}`}
                    onClick={() => downloadEpisode(ep)}
                    disabled={isDownloaded || isDownloading}
                    title={isDownloaded ? 'Déjà téléchargé' : 'Télécharger pour écoute hors ligne'}
                  >
                    {isDownloaded ? '✓' : (isDownloading ? '…' : '⬇')}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {view === 'downloads' && (
        <ul className="podcast-list podcast-episode-list">
          {downloads.length === 0 && <li className="podcast-empty">Aucun épisode téléchargé</li>}
          {downloads.map((d) => (
            <li key={d.id} className={`podcast-episode-item ${currentEpisode?.id === d.id ? 'playing' : ''}`}>
              <div className="podcast-episode-main" onClick={() => playDownload(d)}>
                <span className="podcast-episode-indicator">{currentEpisode?.id === d.id ? '▶' : '○'}</span>
                <div className="podcast-episode-text">
                  <div className="podcast-episode-title">{d.title}</div>
                  <div className="podcast-episode-meta">{d.podcastName}{d.size ? ` · ${formatSize(d.size)}` : ''}</div>
                </div>
              </div>
              <button className="podcast-download-btn delete" onClick={() => removeDownload(d.id)} title="Supprimer">✕</button>
            </li>
          ))}
        </ul>
      )}

      <div className={`podcast-player-bar ${currentEpisode ? 'visible' : ''}`}>
        <div className="podcast-player-title">
          {currentEpisode ? `${currentEpisode.title}${currentEpisode.podcastName ? ' — ' + currentEpisode.podcastName : ''}` : ''}
          {currentEpisode?.isOffline && <span className="podcast-offline-badge">hors ligne</span>}
        </div>
        <audio
          key={currentEpisode?.id}
          src={currentEpisode?.src}
          controls
          autoPlay
          className="podcast-audio"
        />
      </div>
    </div>
  );
};

export default Podcasts;
